import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import axios from 'axios';
import SuccessModal from './SuccessModal';

const EchoCardiogramContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 12px;
  width: 100%;
`;

const SectionTitle = styled.h2`
  width: 100%;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 17px;
  display: flex;
  align-items: center;
  color: #6D6D71;
`;

const ContentBox = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  background: #FFFFFF;
  border: 1px solid #C5C5C6;
  border-radius: 12px;
  padding: 16px;
`;

const FieldsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: calc(25% - 12px);
  min-width: 150px;

  @media (max-width: 1200px) {
    width: calc(33.33% - 11px);
  }

  @media (max-width: 900px) {
    width: calc(50% - 8px);
  }

  @media (max-width: 600px) {
    width: 100%;
  }
`;

const FormLabel = styled.label`
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: #2C2C2D;
`;

const FormInput = styled.input`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  height: 36px;
  background: ${props => (props.readOnly ? '#F4F4F5' : '#FFFFFF')};
  /* R-1.2 red on a rejected field, R-1.3 amber on an open data query. */
  border: 1px solid ${props => (props.hasError ? '#D0342C' : props.hasWarning ? '#C77700' : '#A7A7AA')};
  border-radius: 8px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: ${props => (props.readOnly ? '#6D6D71' : '#2C2C2D')};
  cursor: ${props => (props.readOnly ? 'not-allowed' : 'text')};

  &::placeholder {
    color: #A7A7AA;
  }
`;

// R-3.1 — the six core measurements carry the same asterisk as other mandatory fields.
const RequiredMark = styled.span`
  color: #D0342C;
  margin-right: 4px;
`;

const Tag = styled.span`
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 6px;
  font-family: 'Sofia Sans', sans-serif;
  font-size: 11px;
  line-height: 15px;
  font-weight: 500;
  background: ${props => props.background || '#EEF4FB'};
  color: ${props => props.color || '#2575BD'};
`;

const FieldMessage = styled.div`
  font-family: 'Sofia Sans', sans-serif;
  font-size: 12px;
  line-height: 15px;
  margin-top: 4px;
  color: ${props => (props.tone === 'error' ? '#D0342C' : props.tone === 'warning' ? '#C77700' : '#6D6D71')};
`;

const NotAvailableRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 6px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Sofia Sans', sans-serif;
  font-size: 12px;
  line-height: 15px;
  color: #6D6D71;
  cursor: pointer;
`;

const ReasonInput = styled(FormInput)`
  height: 30px;
  font-size: 12px;
`;

const Banner = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding: 12px 16px;
  border-radius: 8px;
  font-family: 'Sofia Sans', sans-serif;
  font-size: 13px;
  line-height: 18px;
  border: 1px solid ${props => (props.tone === 'error' ? '#F0C4C1' : props.tone === 'warning' ? '#F0DCB8' : '#C9DDF0')};
  background: ${props => (props.tone === 'error' ? '#FDF3F2' : props.tone === 'warning' ? '#FFF9EF' : '#F3F8FC')};
  color: ${props => (props.tone === 'error' ? '#8C221C' : props.tone === 'warning' ? '#7A4A00' : '#1C5C94')};

  ul {
    margin: 6px 0 0;
    padding-left: 18px;
  }
`;

const CompletenessRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-family: 'Sofia Sans', sans-serif;
  font-size: 13px;
  line-height: 18px;
  color: #6D6D71;
`;

const Divider = styled.hr`
  width: 100%;
  border: none;
  border-top: 1px solid #E2E2E3;
  margin: 16px 0;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: #2C2C2D;
`;

const RadioInput = styled.input`
  width: 16px;
  height: 16px;
`;

const SaveButton = styled.button`
  display: flex;
  width: 230px;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 15px 15px;
  gap: 12px;
  background: #2575BD;
  border: none;
  border-radius: 16px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 22px;
  text-align: center;
  color: #FFFFFF;
  cursor: pointer;
  margin-top: 20px;
  &:hover {
    background-color: ${props => props.disabled ? '#E0F0FF' : '#1c5c94'};
  }
`;


const SubSectionTitle = styled(SectionTitle)`
  font-size: 16px;
  color: #4A4A4D;
  margin-top: 16px;
`;

const FormSelect = styled.select`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  height: 36px;
  background: #FFFFFF;
  border: 1px solid #A7A7AA;
  border-radius: 8px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: #2C2C2D;

  &::placeholder {
    color: #A7A7AA;
  }
`;

const FormTextArea = styled.textarea`
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  padding: 8px 12px;
  min-height: 80px;
  background: #FFFFFF;
  border: 1px solid #A7A7AA;
  border-radius: 8px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 17px;
  color: #2C2C2D;
  resize: vertical;

  &::placeholder {
    color: #A7A7AA;
  }
`;

const FullWidthFormField = styled(FormField)`
  width: 100%;
`;

// E-3 §4.1 (R-3.1) — the six core LV/RV measurements are mandatory for every
// participant, healthy control or VHD. Paths match the server's
// echocardiogram_completeness.mandatory list exactly.
const MANDATORY_CORE = [
  { section: 'ventricular_measurements', field: 'ejection_fraction', label: 'Ejection Fraction', unit: '%' },
  { section: 'ventricular_measurements', field: 'stroke_volume', label: 'Stroke Volume', unit: 'mL' },
  { section: 'ventricular_measurements', field: 'lvedd', label: 'LVEDD', unit: 'mm' },
  { section: 'ventricular_measurements', field: 'lvesd', label: 'LVESD', unit: 'mm' },
  { section: 'ventricular_measurements', field: 'lv_wall_thickness', label: 'LV Wall Thickness', unit: 'mm' },
  // allowsDeviation renders a "± <deviation>" input beside the value, stored as
  // `<field>_deviation` in the same section. Requested by AIIMS at the July 2026 site
  // visit: PASP is estimated from the TR jet plus an assumed right atrial pressure, so
  // their report carries a tolerance rather than a single number.
  //
  // Deliberately a generic flag rather than a PASP special case — RVSP is the same
  // TR-jet-derived measurement and is the likely next request.
  //
  // NOT CIP-traceable. The CIP Echocardiogram form defines no deviation field, so this
  // widens the eCRF relative to the CIP — the mirror image of E-2, which narrows it.
  // Needs the same documented rationale or amendment as Appendix D Q8 before go-live.
  { section: 'ventricular_measurements', field: 'pasp', label: 'PASP', unit: 'mmHg', allowsDeviation: true },
];
const mandatoryPath = ({ section, field }) => `${section}.${field}`;

// The deviation is optional even where its parent measurement is mandatory (R-3.1):
// PASP is filled in under 2% of records today, and a second hard gate on it would block
// entry further. A blank deviation means "not stated on the report", not zero.
const deviationField = (field) => `${field}_deviation`;

// §4.2 (R-3.2) — presented for every participant regardless of group, and never
// blocking (R-3.4). Pulmonary is shown but excluded from the preferred count while
// R-3.5 / Appendix D Q4 is open.
const PREFERRED_VALVES = ['aortic', 'mitral', 'tricuspid'];
const PREFERRED_MEASUREMENTS = ['valve_area', 'peak_velocity', 'peak_gradient', 'mean_gradient', 'abnormality'];

// R-3.3 — a "not available on source report" reason must be more than a keystroke.
const REASON_MIN_LENGTH = 3;

const isBlank = (value) => value === undefined || value === null || String(value).trim() === '';

const EchoCardiogram = ({ participantId, formData, onFormDataChange, onSaveSuccess }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showErrors, setShowErrors] = useState(false);
  // Populated from the server's 422 field_errors / 200 warnings so the form shows
  // exactly what the API decided rather than a second guess at it.
  const [fieldErrors, setFieldErrors] = useState({});
  const [fieldWarnings, setFieldWarnings] = useState({});
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    const fetchEchoData = async () => {
      try {
        if (!participantId) {
          console.error('No participant ID provided');
          return;
        }

        const token = localStorage.getItem('token');
        if (!token) {
          console.error('No authentication token found');
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}participant/get-participant-info?participant_id=${participantId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data?.echocardiogram) {
          const echoData = response.data.echocardiogram;
          const initialData = {
            ventricular_measurements: echoData.ventricular_measurements || {},
            additional_measurements: echoData.additional_measurements || {},
            mitral_valve: echoData.mitral_valve || {},
            tricuspid_valve: echoData.tricuspid_valve || {},
            aortic_valve: echoData.aortic_valve || {},
            pulmonary_valve: echoData.pulmonary_valve || {},
            // R-3.3 — path -> reason for fields explicitly unavailable on the
            // source report. Kept out of the measurement blocks so it round-trips
            // to the server untouched.
            not_available: echoData.not_available || {},
            additional_remarks: echoData.additional_remarks || ''
          };
          onFormDataChange(initialData);
        }
      } catch (error) {
        console.error('Error fetching echocardiogram data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEchoData();
  }, [participantId]);

  const handleChange = (section, field, value) => {
    const updatedData = {
      ...formData,
      [section]: {
        ...formData[section],
        [field]: value,
      },
    };
    onFormDataChange(updatedData);
  };

  const notAvailable = formData.not_available || {};

  // R-3.3 — marking a field unavailable clears any value it held, so a record can
  // never be both "not measurable" and populated (the server rejects that).
  const toggleNotAvailable = (section, field, checked) => {
    const path = `${section}.${field}`;
    const next = { ...notAvailable };
    if (checked) next[path] = next[path] || '';
    else delete next[path];
    onFormDataChange({
      ...formData,
      not_available: next,
      // Any ± deviation is cleared alongside the value: a tolerance on a measurement
      // that was never reported would be meaningless, and would otherwise persist
      // invisibly behind the disabled input.
      [section]: {
        ...formData[section],
        ...(checked ? { [field]: '', [deviationField(field)]: '' } : {}),
      },
    });
  };

  const setNotAvailableReason = (section, field, reason) => {
    onFormDataChange({ ...formData, not_available: { ...notAvailable, [`${section}.${field}`]: reason } });
  };

  /**
   * @param options.required     R-3.1 — one of the six core measurements.
   * @param options.readOnly     R-1.5 — computed server-side; shown, not edited.
   * @param options.preferred    R-3.4 — visible affordance, never blocks.
   * @param options.allowsDeviation  Renders "± <deviation>" beside the value, stored as
   *                                 `<field>_deviation`. Always optional.
   * @param options.freeText     Accepts alphanumeric input instead of digits only.
   *                             Used for RVSP, where reports carry ranges and
   *                             qualifiers rather than a single number.
   */
  const renderTextField = (section, field, label, unit, options = {}) => {
    const {
      required = false,
      readOnly = false,
      preferred = false,
      allowsDeviation = false,
      freeText = false,
    } = options;
    const path = `${section}.${field}`;
    const stored = formData[section]?.[field] ?? '';
    const isUnavailable = Object.prototype.hasOwnProperty.call(notAvailable, path);

    const devField = deviationField(field);
    const devPath = `${section}.${devField}`;
    const devStored = formData[section]?.[devField] ?? '';
    const devError = fieldErrors[devPath];
    const devWarning = fieldWarnings[devPath];

    // Only the checks intrinsic to a "±" are done here. The absolute bounds live in the
    // server's validation ruleset (R-1.10 forbids hardcoding ranges in the form), so
    // "value + deviation exceeds the maximum" is the server's call, not ours.
    const deviationIssue = (() => {
      if (!allowsDeviation || isBlank(devStored)) return null;
      const dev = Number(devStored);
      if (Number.isNaN(dev)) return 'Deviation must be a number.';
      if (dev < 0) return 'Deviation is a ± magnitude and cannot be negative.';
      if (!isBlank(stored)) {
        const base = Number(stored);
        if (!Number.isNaN(base) && dev > base) {
          return `A deviation of ${dev} exceeds the value itself (${base}), which would imply a negative ${label}.`;
        }
      }
      return null;
    })();

    const value = stored;

    const error = fieldErrors[path];
    const warning = fieldWarnings[path];
    const missing = required && !isUnavailable && isBlank(stored);

    return (
      <FormField>
        <FormLabel>
          {required && <RequiredMark>*</RequiredMark>}
          {label}
          {preferred && <Tag>Preferred</Tag>}
          {readOnly && <Tag background="#F0F0F1" color="#6D6D71">Calculated</Tag>}
        </FormLabel>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <FormInput
            type="text"
            value={value}
            readOnly={readOnly || isUnavailable}
            hasError={!!error || (showErrors && missing)}
            hasWarning={!!warning}
            onChange={(e) => {
              if (readOnly || isUnavailable) return;
              const newValue = e.target.value;
              // freeText fields (RVSP) accept ranges and qualifiers, so the
              // digits-only guard does not apply to them.
              if (freeText || newValue === '' || !isNaN(newValue)) {
                handleChange(section, field, newValue);
              }
            }}
            placeholder={isUnavailable ? 'Not available on source report' : `Enter ${label.toLowerCase()}`}
          />
          {allowsDeviation && (
            <>
              <span
                aria-hidden="true"
                style={{ margin: '0 8px', fontSize: '15px', color: isUnavailable ? '#B0B0B4' : '#3C3C43' }}
              >
                ±
              </span>
              <FormInput
                type="text"
                aria-label={`${label} deviation`}
                value={devStored}
                readOnly={isUnavailable}
                hasError={!!devError || !!deviationIssue}
                hasWarning={!!devWarning}
                style={{ maxWidth: '90px' }}
                onChange={(e) => {
                  if (isUnavailable) return;
                  const newValue = e.target.value;
                  if (newValue === '' || !isNaN(newValue)) {
                    handleChange(section, devField, newValue);
                  }
                }}
                placeholder={isUnavailable ? '—' : 'deviation'}
              />
            </>
          )}
          {unit && <span style={{ marginLeft: '8px', fontSize: '14px' }}>{unit}</span>}
        </div>

        {readOnly && <FieldMessage>Calculated by the server from the fields above.</FieldMessage>}
        {allowsDeviation && !isUnavailable && (
          <FieldMessage>Optional. Enter the ± tolerance if the source report gives one.</FieldMessage>
        )}
        {deviationIssue && <FieldMessage tone="error">{deviationIssue}</FieldMessage>}
        {!deviationIssue && devError && <FieldMessage tone="error">{devError}</FieldMessage>}
        {!deviationIssue && !devError && devWarning && (
          <FieldMessage tone="warning">{devWarning}</FieldMessage>
        )}

        {error && <FieldMessage tone="error">{error}</FieldMessage>}
        {!error && warning && <FieldMessage tone="warning">{warning}</FieldMessage>}
        {showErrors && missing && !error && (
          <FieldMessage tone="error">
            Required. Enter a value, or tick "not available on source report" and give a reason.
          </FieldMessage>
        )}

        {required && (
          <NotAvailableRow>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={isUnavailable}
                onChange={(e) => toggleNotAvailable(section, field, e.target.checked)}
              />
              Not available on source report
            </CheckboxLabel>
            {isUnavailable && (
              <>
                <ReasonInput
                  type="text"
                  value={notAvailable[path] || ''}
                  hasError={showErrors && (notAvailable[path] || '').trim().length < REASON_MIN_LENGTH}
                  onChange={(e) => setNotAvailableReason(section, field, e.target.value)}
                  placeholder="Reason (required), e.g. not reported on source echo"
                />
                {showErrors && (notAvailable[path] || '').trim().length < REASON_MIN_LENGTH && (
                  <FieldMessage tone="error">A reason of at least {REASON_MIN_LENGTH} characters is required.</FieldMessage>
                )}
              </>
            )}
          </NotAvailableRow>
        )}
      </FormField>
    );
  };

  const renderRadioGroup = (section, field, options) => {
    const value = formData[section]?.[field] || '';

    return (
      <RadioGroup>
        {options.map((option) => (
          <RadioLabel key={option}>
            <RadioInput
              type="radio"
              name={`${section}_${field}`}
              value={option}
              checked={value === option}
              onChange={() => handleChange(section, field, option)}
            />
            {option}
          </RadioLabel>
        ))}
      </RadioGroup>
    );
  };

  const renderSelectField = (section, field, label, options, selectOptions = {}) => {
    const value = formData[section]?.[field] ?? '';

    return (
      <FormField>
        <FormLabel>
          {label}
          {selectOptions.preferred && <Tag>Preferred</Tag>}
        </FormLabel>
        <FormSelect
          value={value}
          onChange={(e) => handleChange(section, field, e.target.value)}
        >
          <option value="">Select...</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </FormSelect>
      </FormField>
    );
  };

  // R-3.1 — the section cannot be signed off with any core measurement blank, or
  // marked unavailable without a reason.
  const outstandingCore = MANDATORY_CORE.filter((entry) => {
    const path = mandatoryPath(entry);
    if (!isBlank(formData[entry.section]?.[entry.field])) return false;
    const reason = notAvailable[path];
    return reason === undefined || String(reason).trim().length < REASON_MIN_LENGTH;
  });

  // R-3.4 — reported alongside, but never gates the save.
  const preferredFilled = PREFERRED_VALVES.reduce((total, valve) => total + PREFERRED_MEASUREMENTS.filter(
    (measurement) => !isBlank(formData[`${valve}_valve`]?.[measurement])

  ).length, 0);
  const preferredTotal = PREFERRED_VALVES.length * PREFERRED_MEASUREMENTS.length;

  // A ± deviation is optional, but if one is entered it must be coherent. Checked here
  // as well as at render time so the save cannot succeed on a value the field is
  // already showing as an error.
  const invalidDeviations = MANDATORY_CORE.filter((entry) => {
    if (entry.allowsDeviation !== true) return false;
    const dev = formData[entry.section]?.[deviationField(entry.field)];
    if (isBlank(dev)) return false;
    const devNum = Number(dev);
    if (Number.isNaN(devNum) || devNum < 0) return true;
    const base = Number(formData[entry.section]?.[entry.field]);
    return !Number.isNaN(base) && devNum > base;
  });

  const handleSave = async () => {
    setFieldErrors({});
    setSaveError('');

    if (invalidDeviations.length > 0) {
      setShowErrors(true);
      setSaveError(
        `Check the ± deviation on ${invalidDeviations.map((entry) => entry.label).join(', ')}. ` +
        'A deviation must be zero or greater and cannot exceed the measurement itself.'
      );
      return;
    }

    if (outstandingCore.length > 0) {
      setShowErrors(true);
      setSaveError(
        `${outstandingCore.length} mandatory measurement${outstandingCore.length > 1 ? 's' : ''} outstanding: ` +
        `${outstandingCore.map((entry) => entry.label).join(', ')}. ` +
        'Enter a value, or mark the field not available with a reason.'
      );
      return;
    }
    setShowErrors(false);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}participant/update-echocardiogram`,
        { participant_id: participantId, echocardiogram: formData },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // R-1.5 / R-3.6 — the server owns the calculated and derived values, so the
      // form takes them back rather than keeping its own copy.
      if (response.data?.echocardiogram) {
        onFormDataChange({ ...formData, ...response.data.echocardiogram });
      }

      // R-1.3 — soft findings come back as warnings; render them amber against the
      // field they belong to.
      const warnings = {};
      (response.data?.warnings || []).forEach((warning) => {
        if (!warning.path) return;
        warnings[warning.path.replace(/^echocardiogram\./, '')] = warning.detail || warning.message;
      });
      setFieldWarnings(warnings);

      setIsModalOpen(true);
    } catch (error) {
      console.error('Error saving Echocardiogram data:', error);
      // R-1.2 — a 422 carries a per-field error array; map it onto the fields.
      const data = error.response?.data;
      if (error.response?.status === 422 && Array.isArray(data?.field_errors)) {
        const errors = {};
        data.field_errors.forEach((fieldError) => {
          if (fieldError.path) errors[fieldError.path.replace(/^echocardiogram\./, '')] = fieldError.message;
        });
        setFieldErrors(errors);
        setSaveError(data.errors.length > 1
          ? `${data.errors.length} fields need correcting before this section can be saved.`
          : data.error);
      } else {
        setSaveError(data?.error || 'Could not save echocardiogram data.');
      }
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    // Call onSaveSuccess here, after the modal is closed
    onSaveSuccess();
  };

  if (isLoading) {
    return <div>Loading echocardiogram data...</div>;
  }

  return (
    <EchoCardiogramContainer>
      <SectionContainer>
        <SectionTitle>ECHOCARDIOGRAM MEASUREMENTS</SectionTitle>
        <ContentBox>
          <CompletenessRow>
            <span>
              <strong>Core measurements:</strong>{' '}
              {MANDATORY_CORE.length - outstandingCore.length} / {MANDATORY_CORE.length} addressed
              {outstandingCore.length > 0 && ` — outstanding: ${outstandingCore.map((e) => e.label).join(', ')}`}
            </span>
            <span>
              <strong>Preferred valve measurements:</strong> {preferredFilled} / {preferredTotal}
            </span>
          </CompletenessRow>
          {saveError && <Banner tone="error">{saveError}</Banner>}
          {Object.keys(fieldWarnings).length > 0 && (
            <Banner tone="warning">
              Saved. {Object.keys(fieldWarnings).length} value{Object.keys(fieldWarnings).length > 1 ? 's' : ''} outside
              the expected range — please confirm the fields highlighted in amber.
            </Banner>
          )}

          <FieldsContainer>
            {MANDATORY_CORE.map((entry) => (
              <React.Fragment key={entry.field}>
                {renderTextField(entry.section, entry.field, entry.label, entry.unit, {
                  required: true,
                  allowsDeviation: entry.allowsDeviation === true,
                })}
              </React.Fragment>
            ))}
          </FieldsContainer>

          <Divider />

          <SubSectionTitle>Additional Measurements</SubSectionTitle>
          <FieldsContainer>
            {renderTextField('additional_measurements', 'e_a_ratio', 'E/A Ratio', '')}
            {renderTextField('additional_measurements', 'e_e_ratio', 'E/e\' Ratio', '')}
            {renderTextField('additional_measurements', 'aortic_root_dimension', 'Aortic Root', 'mm')}
            {renderTextField('additional_measurements', 'left_atrial_diameter', 'Left Atrial Diameter', 'mm')}
            {/* R-1.5 — LA/Ao is computed from the two fields above and is read-only. */}
            {/* Entered manually. The server-side calculation was removed at the site's
              request, Aug 2026 — it disagreed with the source report and, being
              non-overridable, could not be corrected. Reverses R-1.5 for this field. */}
            {renderTextField('additional_measurements', 'la_aorta_ratio', 'LA Aorta Ratio', '')}
          </FieldsContainer>
        </ContentBox>
      </SectionContainer>

      <SectionContainer>
        <SectionTitle>VALVE MEASUREMENTS</SectionTitle>
        <ContentBox>
          <Banner>
            Captured wherever the source echocardiogram report provides them, for healthy controls and VHD
            participants alike. Tagged <Tag>Preferred</Tag> — they never block saving, but the aortic, mitral and
            tricuspid figures are reported as a separate completeness measure.
          </Banner>
          {/* R-3.2 — all valves render for every participant; nothing is hidden by group. */}
          {['mitral', 'tricuspid', 'aortic', 'pulmonary'].map((valve) => {
            const preferred = PREFERRED_VALVES.includes(valve);
            return (
              <React.Fragment key={valve}>
                <SectionTitle>
                  {valve.charAt(0).toUpperCase() + valve.slice(1)} Valve
                  {/* R-3.5 — pulmonary fill is near zero; scope is pending Appendix D Q4,
                      so it stays on the form but out of the preferred count. */}
                  {!preferred && <Tag background="#F0F0F1" color="#6D6D71">Scope under review</Tag>}
                </SectionTitle>
                <FieldsContainer>
                  {renderTextField(`${valve}_valve`, 'valve_area', 'Valve Area', 'cm²', { preferred })}
                  {renderTextField(`${valve}_valve`, 'peak_velocity', 'Peak Velocity', 'm/s', { preferred })}
                  {/* Entered manually. The 4v² pre-fill was removed at the site's
                      request, Aug 2026 — the derived figure disagreed with the
                      source echo report. Reverses R-3.6. */}
                  {renderTextField(`${valve}_valve`, 'peak_gradient', 'Peak Gradient', 'mmHg', { preferred })}
                  {renderTextField(`${valve}_valve`, 'mean_gradient', 'Mean Gradient', 'mmHg', { preferred })}
                  {/* Alphanumeric at the site's request: reports carry ranges
                      ("30-35"), qualifiers ("<25") and "not estimable" where there
                      is no adequate TR jet. */}
                  {valve === 'tricuspid' && renderTextField(`${valve}_valve`, 'rvsp', 'Right Ventricular Systolic Pressure', 'mmHg', { freeText: true })}
                </FieldsContainer>

                <FieldsContainer>
                  <FormField>
                    <FormLabel>Regurgitation Grade</FormLabel>
                    {renderRadioGroup(`${valve}_valve`, 'regurgitation_grade', ['None', 'Trivial', 'Mild', 'Moderate', 'Severe'])}
                  </FormField>
                  <FormField>
                    <FormLabel>Stenosis Grade</FormLabel>
                    {renderRadioGroup(`${valve}_valve`, 'stenosis_grade', ['None', 'Trivial', 'Mild', 'Moderate', 'Severe'])}
                  </FormField>
                  {renderSelectField(`${valve}_valve`, 'abnormality', 'Abnormality', ['No', 'Yes'], { preferred })}
                </FieldsContainer>
                {valve !== 'pulmonary' && <Divider />}
              </React.Fragment>
            );
          })}
        </ContentBox>
      </SectionContainer>

      <SectionContainer>
        <SectionTitle>ADDITIONAL REMARKS</SectionTitle>
        <ContentBox>
          <FullWidthFormField>
            <FormLabel>Additional Remarks</FormLabel>
            <FormTextArea
              value={formData.additional_remarks || ''}
              onChange={(e) => onFormDataChange({ ...formData, additional_remarks: e.target.value })}
              placeholder="Enter any additional remarks or observations..."
            />
          </FullWidthFormField>
        </ContentBox>
      </SectionContainer>

      <SaveButton onClick={handleSave}>Save and Next</SaveButton>

      <SuccessModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        title="Echocardiogram Data Saved"
        message={`Echocardiogram data has been successfully saved for Participant ID: ${participantId}`}
      />
    </EchoCardiogramContainer>
  );
};

export default EchoCardiogram;
